import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";
import type { ScrapFormValues } from "@/features/scrap/schema";
import type { ScrapEntryListRow } from "@/features/scrap/types";

function money(v: unknown) {
  const n = typeof v === "number" ? v : Number(v ?? 0);
  return Number.isNaN(n) ? 0 : n;
}

export function useScrapEntries() {
  return useQuery({
    queryKey: ["scrap_entries"],
    enabled: isSupabaseConfigured,
    queryFn: async (): Promise<ScrapEntryListRow[]> => {
      const { data, error } = await supabase
        .from("scrap_entries")
        .select(
          `*,
          materials(name),
          stock_locations(code, name),
          inventory_batches(batch_no)`,
        )
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      return (data ?? []).map((row) => {
        const r = row as Record<string, unknown>;
        const mat = r.materials as { name?: string } | null;
        const loc = r.stock_locations as { code?: string; name?: string } | null;
        const batch = r.inventory_batches as { batch_no?: string } | null;
        return {
          id: String(r.id),
          inventory_batch_id: (r.inventory_batch_id as string | null) ?? null,
          material_id: String(r.material_id),
          location_id: String(r.location_id),
          weight: money(r.weight),
          quantity: money(r.quantity),
          reason: String(r.reason ?? ""),
          notes: (r.notes as string | null) ?? null,
          created_by: (r.created_by as string | null) ?? null,
          created_at: String(r.created_at),
          material_name: mat?.name ?? null,
          location_code: loc?.code ?? null,
          location_name: loc?.name ?? null,
          batch_no: batch?.batch_no ?? null,
        };
      });
    },
  });
}

export function useCreateScrapEntry() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (values: ScrapFormValues) => {
      const batchId = values.inventory_batch_id?.trim() || null;
      const weight = money(values.weight);
      const quantity = money(values.quantity);

      if (batchId) {
        const { data: batch, error: bErr } = await supabase
          .from("inventory_batches")
          .select("id, weight, quantity, location_id, batch_status")
          .eq("id", batchId)
          .single();
        if (bErr) throw bErr;
        const avail = money(batch.weight);
        if (weight > avail + 1e-6) {
          throw new Error(`Scrap weight ${weight} exceeds batch weight ${avail}`);
        }
      }

      const { data: entry, error: insErr } = await supabase
        .from("scrap_entries")
        .insert({
          inventory_batch_id: batchId,
          material_id: values.material_id,
          location_id: values.location_id,
          weight,
          quantity,
          reason: values.reason.trim(),
          notes: values.notes?.trim() || null,
          created_by: user?.id ?? null,
        })
        .select("id")
        .single();
      if (insErr) throw insErr;

      if (batchId) {
        const { data: batch, error: bErr } = await supabase
          .from("inventory_batches")
          .select("id, weight, quantity, location_id")
          .eq("id", batchId)
          .single();
        if (bErr) throw bErr;

        const newWeight = Math.max(0, money(batch.weight) - weight);
        const newQty = Math.max(0, money(batch.quantity) - quantity);
        const consumed = newWeight <= 1e-6 && newQty <= 1e-6;

        const { error: updErr } = await supabase
          .from("inventory_batches")
          .update({
            weight: newWeight,
            quantity: newQty,
            ...(consumed ? { batch_status: "consumed" } : {}),
            updated_at: new Date().toISOString(),
          })
          .eq("id", batchId);
        if (updErr) throw updErr;

        const { error: movErr } = await supabase.from("inventory_movements").insert({
          inventory_batch_id: batchId,
          kind: "issue",
          quantity_delta: -quantity,
          weight_delta: -weight,
          quantity_after: newQty,
          weight_after: newWeight,
          from_location_id: batch.location_id as string,
          source_type: "scrap_entry",
          source_id: entry.id,
          notes: values.reason.trim(),
          created_by: user?.id ?? null,
        });
        if (movErr) throw movErr;
      }

      return entry;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["scrap_entries"] });
      await queryClient.invalidateQueries({ queryKey: ["inventory_batches"] });
      await queryClient.invalidateQueries({ queryKey: ["inventory_movements"] });
      toast.success("Scrap recorded");
    },
    onError: (e: Error) => toast.error(e.message || "Failed to record scrap"),
  });
}
